import { useRef } from 'react';
import { Divider } from '@dsg-tech/homefield-react';
import { HomefieldIconListMenu } from '@dsg-tech/homefield-react-icons';
import { useAppSelector } from 'store/store';
import { KEY_CODES } from 'utility/constants';
import { TrendingSearch, PrimaryCategoryData, NikeConnected } from 'types';
import { breakpointSelector, getFeatureFlagByNameSelector } from 'store/selectors';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import ConsolidatedNavTrendingSearch from './ConsolidatedNavTrendingSearch';
import ConsolidatedNavL0Links from './ConsolidatedNavL0Links';
import MenuPopover, { useMenuPopover } from '../MenuPopover/MenuPopover';
import './ConsolidatedNav.scss';

interface ConsolidatedNavProps {
  content: TrendingSearch | undefined;
  setShowDrawer: () => void;
  navBarData: PrimaryCategoryData[] | undefined;
  nikeConnected: NikeConnected;
  viewTopNavAnalyticsEventHandler: () => void;
}

const ConsolidatedNav = ({
  content,
  setShowDrawer,
  navBarData,
  nikeConnected,
  viewTopNavAnalyticsEventHandler,
}: ConsolidatedNavProps) => {
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const hamburgerMenuRef = useRef(null);
  const { showMenuPopoverFlag, newTaxoPopoverClosed, taxoPopOverHasBeenClosed } = useMenuPopover();
  const allCategoriesFlag = useAppSelector(getFeatureFlagByNameSelector('allCategories'));
  const l0LinksFlag = useAppSelector(getFeatureFlagByNameSelector('consolidatedNavL0Links'));
  const trendingSearchInConsolidatedNavFlag = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchInConsolidatedNav'),
  );

  return (
    <>
      {content && content !== null && (
        <div className="consolidated-nav-container">
          <div
            className={`consolidated-nav consolidated-nav-${viewPortBreakpoint} hmf-display-flex hmf-flex-row hmf-justify-content-between hmf-mt-lg-0 hmf-m-auto`}>
            {/* Gradient background */}
            <div className="consolidated-header-right-gradient" />
            <div className="consolidated-header-link hmf-ml-xs hmf-mr-xxs hmf-display-flex hmf-align-items-center scrollable">
              {/* Only display the All Categories button in desktop view */}
              {allCategoriesFlag && (
                <div className="hmf-display-none hmf-display-lg-flex">
                  <button
                    type="button"
                    ref={hamburgerMenuRef}
                    className="all-categories-button hmf-display-flex hmf-flex-row hmf-justify-content-center hmf-align-items-center hmf-px-0"
                    tabIndex={0}
                    aria-label="Open menu"
                    onClick={() => {
                      setShowDrawer();
                      taxoPopOverHasBeenClosed();
                      viewTopNavAnalyticsEventHandler();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
                        setShowDrawer();
                        viewTopNavAnalyticsEventHandler();
                      }
                    }}>
                    <HomefieldIconListMenu aria-label="Open menu icon" width={18} height={18} />
                    <p className="hmf-my-0 hmf-ml-xs hmf-mr-m hmf-display-flex hmf-align-items-center hmf-header-xs">
                      All Categories
                    </p>
                  </button>

                  <Divider vertical />
                  {showMenuPopoverFlag && !newTaxoPopoverClosed && (
                    <ErrorBoundaryWithLogger componentId="ConsolidatedNav-MenuPopover">
                      <MenuPopover hamburgerMenuRef={hamburgerMenuRef} component="allCategories" />
                    </ErrorBoundaryWithLogger>
                  )}
                </div>
              )}

              {l0LinksFlag && (
                <div className="hmf-display-none hmf-display-lg-flex">
                  <ErrorBoundaryWithLogger componentId="ConsolidatedNav-ConsolidatedNavL0Links">
                    <ConsolidatedNavL0Links
                      navBarData={navBarData}
                      setShowDrawer={setShowDrawer}
                      nikeConnected={nikeConnected}
                    />
                  </ErrorBoundaryWithLogger>
                </div>
              )}
              {trendingSearchInConsolidatedNavFlag && (
                <div className="hmf-display-inline-flex">
                  <ErrorBoundaryWithLogger componentId="ConsolidatedNav-ConsolidatedNavTrendingSearch">
                    <ConsolidatedNavTrendingSearch trendingSearches={content.cta} />
                  </ErrorBoundaryWithLogger>
                </div>
              )}

              {/* Add below code to make last item visible by applying a white space */}
              {['mobile', 'tablet'].includes(viewPortBreakpoint) && (
                <div className="consolidated-header-empty-space"> </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsolidatedNav;
